import { c as _c } from "react/compiler-runtime";
import { DeepPartial, Overwrite } from 'utility-types';
import { Form as AntdForm } from 'antd';
import { ErrorListProps, FormInstance, FormItemProps, FormListFieldData, FormListOperation, FormListProps, FormProps as AntdFormProps, Rule, RuleObject, RuleRender } from 'antd/es/form';
import { FormLayout } from 'antd/es/form/Form';
import { InternalNamePath, NamePath, Store } from 'antd/es/form/interface';
import cx from 'classnames';
import { ComponentProps, isValidElement, PropsWithChildren, ReactNode, useEffect, useRef } from 'react';
import './styles.scss';
const PrefixCls = 'app-form';
interface FormProps<Values = any> extends Overwrite<AntdFormProps<Values>, {
  initialValues?: DeepPartial<Values>;
}> {
  displayMode?: boolean;
}
export type { FormProps, ErrorListProps, FormItemProps, FormListFieldData, FormListOperation, FormListProps, Rule as FormRule, RuleObject as FormRuleObject, RuleRender as FormRuleRender, InternalNamePath as FormInternalNamePath, NamePath as FormNamePath, FormInstance, FormLayout };

/**
 * This class is a wrap around [antd Form](https://ant.design/components/form/) component extending some of
 * the properties to follow our styling guidelines.
 *
 * Please refer to antd documentation for more details.
 *
 * **Important:** Avoid passing JSX elements as `label` properties in form values. Use plain strings instead.
 * In React 19, JSX elements are frozen objects which can cause issues with form state management.
 *
 * @example
 * ```typescript
 * // ✅ Good - use plain strings
 * form.setFieldValue('transport', { label: 'Bus', value: 'bus' })
 *
 * // ❌ Bad - avoid JSX in labels
 * form.setFieldValue('transport', { label: <span>Bus</span>, value: 'bus' })
 * ```
 */
function InternalForm(t0) {
  const $ = _c(12);
  let className;
  let displayMode;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      displayMode,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = displayMode;
    $[3] = props;
    $[4] = ref;
  } else {
    className = $[1];
    displayMode = $[2];
    props = $[3];
    ref = $[4];
  }
  const t1 = displayMode && "app-form--display-mode";
  let t2;
  if ($[5] !== className || $[6] !== t1) {
    t2 = cx(PrefixCls, t1, className);
    $[5] = className;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== props || $[9] !== ref || $[10] !== t2) {
    t3 = <AntdForm ref={ref} {...props} className={t2} />;
    $[8] = props;
    $[9] = ref;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
function InternalFormItem(t0) {
  const $ = _c(8);
  let label;
  let props;
  if ($[0] !== t0) {
    ({
      label,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = label;
    $[2] = props;
  } else {
    label = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== label) {
    t1 = isValidElement(label) && label.type === LabelWithHint ? label : label ? <span className="app-form-item-label" style={{
      width: "inherit"
    }}>{label}</span> : null;
    $[3] = label;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <AntdForm.Item {...props} label={t1} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
InternalFormItem.useStatus = AntdForm.Item.useStatus;
function LabelWithHint(t0) {
  const $ = _c(7);
  const {
    label,
    hint
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="app-typography">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== hint) {
    t2 = <span className="app-form-item-custom-label-hint app-typography ant-typography-secondary">{hint}</span>;
    $[2] = hint;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="app-form-item-custom-label">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
InternalFormItem.LabelWithHint = LabelWithHint;

/**
 * Resets a dependent form field when its parent dependency changes.
 * Used for cascading selects where child values become invalid when parent changes
 * (e.g., worksite resets when enterprise changes since worksites are enterprise-scoped).
 *
 * @example
 * ```tsx
 * <Form.CascadingFieldReset
 *   dependency={enterpriseId}
 *   onReset={() => setFieldsValue({ worksiteId: undefined })}
 * />
 * ```
 */
function CascadingFieldReset(t0) {
  const $ = _c(6);
  const {
    dependency,
    onReset
  } = t0;
  const prevDependency = useRef(dependency);
  const onResetRef = useRef(onReset);
  let t1;
  let t2;
  if ($[0] !== onReset) {
    t1 = () => {
      onResetRef.current = onReset;
    };
    t2 = [onReset];
    $[0] = onReset;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== dependency) {
    t3 = () => {
      if (prevDependency.current !== undefined && dependency !== prevDependency.current) {
        onResetRef.current();
      }
      prevDependency.current = dependency;
    };
    t4 = [dependency];
    $[3] = dependency;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  return null;
}
type CascadingFieldResetProps = {
  /** The parent value to watch for changes */
  dependency: string | undefined;
  /** Callback fired when dependency changes (e.g., clear dependent form fields) */
  onReset: () => void;
};
type CompoundedComponent = typeof InternalForm & {
  useForm: typeof AntdForm.useForm;
  useFormInstance: typeof AntdForm.useFormInstance;
  useWatch: typeof AntdForm.useWatch;
  Item: typeof InternalFormItem;
  List: typeof AntdForm.List;
  ErrorList: typeof AntdForm.ErrorList;
  Provider: typeof AntdForm.Provider;
  CascadingFieldReset: typeof CascadingFieldReset;

  /** @deprecated Only for warning usage. Do not use. */
  create: () => void;
};
export const Form = InternalForm as CompoundedComponent;
Form.ErrorList = AntdForm.ErrorList;
Form.Item = InternalFormItem;
Form.List = AntdForm.List;
Form.Provider = AntdForm.Provider;
Form.useForm = AntdForm.useForm;
Form.useFormInstance = AntdForm.useFormInstance;
Form.useWatch = AntdForm.useWatch;
Form.CascadingFieldReset = CascadingFieldReset;